import {
  FileIcon,
  SearchIcon,
  SyncIcon,
  DownloadIcon,
} from "@primer/octicons-react";
import {
  Box,
  Label,
  Text,
  TextInput,
  ActionMenu,
  ActionList,
} from "@primer/react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { crmHocSinhApi } from "../../api/crmHocSinhApi";
import { loaiGiayToApi } from "../../api/loaiGiayToApi";
import { ComboboxTruong } from "../../components/combobox-truong";
import PreviewableAvatar from "../../components/hocsinh-avatar-preview/PreviewableAvatar";
import SelectBoxTrangThaiHocSinhMultiple from "../../components/selectbox-trangthai-hocsinh-multiple";
import Button from "../../components/ui/button";
import DataTable from "../../components/ui/data-table";
import TextWithEllipse from "../../components/ui/text-with-ellipse/TextWithEllipse";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../models/ePageBaseStatus";
import { IHocSinhSelectRequest } from "../../models/request/hoc-sinh/IHocSinhSelectRequest";
import { actions } from "../../state/actions/actionsWrapper";
import { RootState } from "../../state/reducers";
import HocSinhProfile from "../hoc-sinh-profile";
import { NotifyHelper } from "../../helpers/toast";

interface GiayToChiTiet {
  chitiet_id: number;
  ten_file: string;
  duong_dan: string;
}

interface GiayTo {
  giayto_id: number;
  loai_giay_to: string;
  is_danop?: boolean;
  danhSachChiTiet?: GiayToChiTiet[];
}

interface HocSinh {
  hocsinh_id: number;
  ma_hs: string;
  ho_ten: string;
  ten_truong: string;
  ten_he: string;
  ten_khoi: string;
  ten_lop: string;
  dm_truong_id: number;
  dm_khoi_id: number;
  dm_he_id: number;
  nam_hoc: string;

  avatar?: string;
  danhSachGiayTo?: GiayTo[];
}

const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

const getCorrectFileUrl = (url: string): string => {
  if (!url) return "";

  if (url.includes("1school.s3.cloudfly.vn")) {
    const matches = url.match(
      /1school\.s3\.cloudfly\.vn\/admission\/IMG\/[^/\s"']+\.[^/\s"']+$/i
    );
    if (matches) {
      return `https://${matches[0]}`;
    }
    if (url.startsWith("https://1school.s3.cloudfly.vn/")) {
      return url;
    }
  }

  if (/^[a-f0-9-]+\.(jpg|jpeg|png|pdf|gif)$/i.test(url)) {
    return `https://1school.s3.cloudfly.vn/admission/IMG/${url}`;
  }

  if (url.startsWith("/admission/IMG/")) {
    return `https://1school.s3.cloudfly.vn${url}`;
  }

  return url;
};

const normalizeText = (text: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
};

const HocSinhGiayTo = ({
  onValueChanged,
}: {
  onValueChanged?: (values: { dm_trangthaihocsinh_ids: string }) => void;
}) => {
  const { status } = useSelector((state: RootState) => state.crmHocSinh);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate, checkAccesiableTo } = useCommonContext();
  const dispatch = useDispatch();
  const [allLoaiGiayTo, setAllLoaiGiayTo] = useState<any[]>([]);

  const { dm_truong_selected_id } = useSelector((x: RootState) => x.common);
  const [dm_trangthaihocsinh_ids, setDmTrangThaiIds] = useState<number[]>([]);
  const [isShowModal, setIsShowModal] = useState(false);
  const [hocSinhEditing, setHocSinhEditing] = useState({ id: 0 });
  const [allHocSinhs, setAllHocSinhs] = useState<HocSinh[]>([]);
  const [filteredHocSinhs, setFilteredHocSinhs] = useState<HocSinh[]>([]);
  const [expandedStudentId, setExpandedStudentId] = useState<number | null>(
    null
  );
  const [searchText, setSearchText] = useState<string>("");
  const [loaiGiayToTheoTruongMap, setLoaiGiayToTheoTruongMap] = useState<
    Record<string, any[]>
  >({});
  const [expandedLoaiGiayToStudentIds, setExpandedLoaiGiayToStudentIds] =
    useState<Set<number>>(new Set());
  const [selectedHocSinhIds, setSelectedHocSinhIds] = useState<number[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const canView = checkAccesiableTo("hoc-sinh/select-all-list-giayto", "POST");
  const canViewLoaiGiayTo = checkAccesiableTo(
    "loai-giay-to/select-all",
    "POST"
  );

  const countTotalFiles = (danhSachGiayTo?: GiayTo[] | null): number => {
    if (!danhSachGiayTo || danhSachGiayTo.length === 0) {
      return 0;
    }

    return danhSachGiayTo.reduce((total, giayTo) => {
      return total + (giayTo.danhSachChiTiet?.length || 0);
    }, 0);
  };

  // Bỏ useEffect này vì đã có handleReload được gọi ở useEffect khác
  // useEffect(() => {
  //   const loadAllHocSinh = async () => {
  //     const payload: IHocSinhSelectRequest = {
  //       nam_hoc: nam_hoc,
  //       searchText: "",
  //       dm_trangthaihocsinh_ids: [],
  //       dm_truong_id: dm_truong_selected_id || 0,
  //       dm_coso_id: dm_coso_id || 1,
  //       dm_khoi_id: 0,
  //       dm_he_id: 0,
  //       dm_lop_id: 0,
  //     };
  //     const res = await crmHocSinhApi.SelectTongHopGiayTo(payload);
  //     if (res.is_success) {
  //       setAllHocSinhs(res.data);
  //       setFilteredHocSinhs(res.data);
  //     }
  //   };

  //   loadAllHocSinh();
  // }, [nam_hoc, dm_truong_selected_id, dm_coso_id]);

  const loadAllLoaiGiayTo = async () => {
    try {
      const res = await loaiGiayToApi.selectAll();

      if (res.is_success) {
        setAllLoaiGiayTo(res.data);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    loadAllLoaiGiayTo();
  }, [nam_hoc, dm_truong_selected_id, dm_coso_id]);

  useEffect(() => {
    const fetchLoaiGiayToForSchools = async () => {
      try {
        const paramsMap: Record<
          string,
          {
            nam_hoc: string;
            dm_truong_id: number;
            dm_khoi_id: number;
            dm_he_id: number;
          }
        > = {};

        allHocSinhs.forEach((hs) => {
          const key = `${hs.nam_hoc || ""}_${hs.dm_truong_id || 0}_${
            hs.dm_khoi_id || 0
          }_${hs.dm_he_id || 0}`;
          paramsMap[key] = {
            nam_hoc: hs.nam_hoc || "",
            dm_truong_id: hs.dm_truong_id || 0,
            dm_khoi_id: hs.dm_khoi_id || 0,
            dm_he_id: hs.dm_he_id || 0,
          };
        });

        // Chỉ gọi API cho những tổ hợp chưa có trong map để tránh gọi lại không cần thiết
        setLoaiGiayToTheoTruongMap((prevMap) => {
          const keysToFetch = Object.keys(paramsMap).filter(
            (key) => !prevMap[key] || prevMap[key].length === 0
          );

          // Gọi API cho những tổ hợp chưa có
          if (keysToFetch.length > 0) {
            Promise.all(
              keysToFetch.map(async (key) => {
                const params = paramsMap[key];
                try {
                  const res = await loaiGiayToApi.select(params);
                  if (res.is_success) {
                    return { key, data: res.data };
                  }
                } catch (error) {
                  // Ignore error
                }
                return null;
              })
            ).then((results) => {
              const updates: Record<string, any[]> = {};
              results.forEach((result) => {
                if (result) {
                  updates[result.key] = result.data;
                }
              });

              if (Object.keys(updates).length > 0) {
                setLoaiGiayToTheoTruongMap((currentMap) => ({
                  ...currentMap,
                  ...updates,
                }));
              }
            });
          }

          return prevMap;
        });
      } catch (error) {
        // Ignore error
      }
    };

    if (allHocSinhs.length > 0) {
      fetchLoaiGiayToForSchools();
    }
  }, [allHocSinhs]);

  useEffect(() => {
    if (!searchText) {
      setFilteredHocSinhs(allHocSinhs);
      return;
    }

    const normalizedSearchText = normalizeText(searchText);

    const filtered = allHocSinhs.filter((student) => {
      if (
        normalizeText(student.ho_ten).includes(normalizedSearchText) ||
        normalizeText(student.ma_hs).includes(normalizedSearchText) ||
        normalizeText(student.ten_truong).includes(normalizedSearchText) ||
        normalizeText(student.ten_khoi).includes(normalizedSearchText) ||
        normalizeText(student.ten_lop).includes(normalizedSearchText) ||
        normalizeText(student.ten_he).includes(normalizedSearchText)
      ) {
        return true;
      }

      if (student.danhSachGiayTo && student.danhSachGiayTo.length > 0) {
        for (const giayTo of student.danhSachGiayTo) {
          if (
            normalizeText(giayTo.loai_giay_to).includes(normalizedSearchText)
          ) {
            return true;
          }

          if (giayTo.danhSachChiTiet && giayTo.danhSachChiTiet.length > 0) {
            for (const chiTiet of giayTo.danhSachChiTiet) {
              if (
                normalizeText(chiTiet.ten_file).includes(normalizedSearchText)
              ) {
                return true;
              }
            }
          }
        }
      }

      return false;
    });

    setFilteredHocSinhs(filtered);
  }, [searchText, allHocSinhs]);

  const handleReload = useCallback(() => {
    const payload: IHocSinhSelectRequest = {
      nam_hoc: nam_hoc,
      searchText: "",
      dm_trangthaihocsinh_ids: dm_trangthaihocsinh_ids,
      dm_truong_id: dm_truong_selected_id,
      dm_coso_id: dm_coso_id,
      dm_khoi_id: 0,
      dm_he_id: 0,
      dm_lop_id: 0,
    };

    const fetchData = async () => {
      const res = await crmHocSinhApi.SelectTongHopGiayTo(payload);
      if (res.is_success) {
        setAllHocSinhs(res.data);
        setFilteredHocSinhs(res.data);
      }
    };

    fetchData();
  }, [nam_hoc, dm_trangthaihocsinh_ids, dm_truong_selected_id, dm_coso_id]);

  // Gọi API khi status hoặc các dependency quan trọng thay đổi - chỉ 1 useEffect để tránh gọi trùng
  useEffect(() => {
    // Gọi khi status cần reload
    if (
      status === ePageBaseStatus.is_not_initialization ||
      status === ePageBaseStatus.is_need_reload
    ) {
      handleReload();
      return;
    }

    // Gọi khi các filter thay đổi (chỉ khi status đã sẵn sàng)
    if (status === ePageBaseStatus.is_completed || status === ePageBaseStatus.is_loading) {
      handleReload();
    }
  }, [status, nam_hoc, dm_truong_selected_id, dm_coso_id, dm_trangthaihocsinh_ids, handleReload]);

  const renderDocumentList = (data: HocSinh) => {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ fontWeight: "bold", mb: 2 }}>Danh sách giấy tờ:</Box>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  padding: "12px",
                  backgroundColor: "#f6f8fa",
                  borderBottom: "1px solid #e1e4e8",
                  textAlign: "left",
                }}
              >
                Tên giấy tờ
              </th>
              <th
                style={{
                  padding: "12px",
                  backgroundColor: "#f6f8fa",
                  borderBottom: "1px solid #e1e4e8",
                  textAlign: "left",
                }}
              >
                Trạng thái
              </th>
              <th
                style={{
                  padding: "12px",
                  backgroundColor: "#f6f8fa",
                  borderBottom: "1px solid #e1e4e8",
                  textAlign: "left",
                }}
              >
                Chi tiết file
              </th>
            </tr>
          </thead>
          <tbody>
            {allLoaiGiayTo.map((loaiGiayTo: any, index: number) => {
              const giayToNop = data.danhSachGiayTo?.find(
                (gt) => gt.loai_giay_to === loaiGiayTo.loai_giay_to
              );
              const isSubmitted = giayToNop?.is_danop === true;

              return (
                <tr key={index}>
                  <td
                    style={{
                      padding: "12px",
                      borderBottom: "1px solid #e1e4e8",
                      width: "30%",
                      verticalAlign: "top",
                    }}
                  >
                    <Box
                      sx={{
                        fontWeight: "500",
                        color: "text.primary",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ mr: 2 }}>
                        <FileIcon size={16} />
                      </Box>
                      {loaiGiayTo.loai_giay_to}
                    </Box>
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      borderBottom: "1px solid #e1e4e8",
                      width: "15%",
                      verticalAlign: "top",
                    }}
                  >
                    {isSubmitted ? (
                      <Label variant="success">Đã nộp</Label>
                    ) : (
                      <Label variant="danger">Chưa nộp</Label>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "12px",
                      borderBottom: "1px solid #e1e4e8",
                      verticalAlign: "top",
                    }}
                  >
                    {isSubmitted &&
                    giayToNop?.danhSachChiTiet &&
                    giayToNop.danhSachChiTiet.length > 0 ? (
                      <Box>
                        {giayToNop.danhSachChiTiet.map(
                          (chiTiet: GiayToChiTiet, chiTietIndex: number) => {
                            const isImage = /\.(jpg|jpeg|png)$/i.test(
                              chiTiet.ten_file
                            );
                            const fileUrl = getCorrectFileUrl(
                              chiTiet.duong_dan
                            );

                            return (
                              <Box
                                key={chiTietIndex}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 2,
                                  pb:
                                    chiTietIndex <
                                    (giayToNop.danhSachChiTiet?.length || 0) - 1
                                      ? 2
                                      : 0,
                                  borderBottom:
                                    chiTietIndex <
                                    (giayToNop.danhSachChiTiet?.length || 0) - 1
                                      ? "1px solid #eaecef"
                                      : "none",
                                }}
                              >
                                {isImage ? (
                                  <img
                                    src={fileUrl}
                                    style={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: 4,
                                      objectFit: "cover",
                                    }}
                                    alt={chiTiet.ten_file}
                                  />
                                ) : (
                                  <FileIcon size={24} />
                                )}
                                <Text
                                  as="a"
                                  href={fileUrl}
                                  target="_blank"
                                  sx={{
                                    color: "#0366d6",
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {chiTiet.ten_file}
                                </Text>
                              </Box>
                            );
                          }
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{ color: "text.secondary", fontStyle: "italic" }}
                      >
                        {isSubmitted
                          ? "Không có file đính kèm"
                          : "Chưa nộp giấy tờ này"}
                      </Box>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    );
  };

  const handleRowClick = (data: HocSinh) => {
    if (expandedStudentId === data.hocsinh_id) {
      setExpandedStudentId(null);
    } else {
      setExpandedStudentId(data.hocsinh_id);
    }
  };

  const isImageFile = (filename: string): boolean => {
    return /\.(jpg|jpeg|png|gif)$/i.test(filename);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleSelectionChanged = (keys: number[]) => {
    setSelectedHocSinhIds(keys);
  };

  const handleExportBaoCao = async () => {
    if (selectedHocSinhIds.length === 0) {
      NotifyHelper.Error("Vui lòng chọn ít nhất một học sinh để xuất báo cáo");
      return;
    }

    try {
      setIsExporting(true);
      const selectedIds = selectedHocSinhIds;

      const res = await crmHocSinhApi.ExportTongHopGiayToHtml({
        hocsinh_ids: selectedIds,
        nam_hoc: nam_hoc,
        dm_coso_id: dm_coso_id,
      });

      if (res.is_success && res.data) {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(res.data);
          printWindow.document.close();
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 250);
          };
        }
        NotifyHelper.Success("Xuất báo cáo thành công!");
      } else {
        NotifyHelper.Error(res.message ?? "Có lỗi xảy ra khi xuất báo cáo");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi xuất báo cáo");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportBanGiao = async () => {
    if (selectedHocSinhIds.length === 0) {
      NotifyHelper.Error("Vui lòng chọn ít nhất một học sinh để xuất bàn giao");
      return;
    }

    try {
      setIsExporting(true);
      const selectedIds = selectedHocSinhIds;

      const res = await crmHocSinhApi.ExportBanGiaoHoSoHtml({
        hocsinh_ids: selectedIds,
        nam_hoc: nam_hoc,
        dm_coso_id: dm_coso_id,
      });

      if (res.is_success && res.data) {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(res.data);
          printWindow.document.close();
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 250);
          };
        }
        NotifyHelper.Success("Xuất bàn giao thành công!");
      } else {
        NotifyHelper.Error(res.message ?? "Có lỗi xảy ra khi xuất bàn giao");
      }
    } catch (error) {
      NotifyHelper.Error("Có lỗi xảy ra khi xuất bàn giao");
    } finally {
      setIsExporting(false);
    }
  };

  if (!canView || !canViewLoaiGiayTo) {
    return (
      <Box sx={{ p: 3, color: "danger.fg" }}>
        Bạn không có quyền truy cập chức năng này.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        overflowX: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Box sx={{ fontSize: "24px", fontWeight: "600", mb: 0 }}>
            {translate("Giấy tờ nộp")}
          </Box>       
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <ActionMenu>
              <ActionMenu.Button
                variant="default"
                disabled={selectedHocSinhIds.length === 0 || isExporting}
              >
                Thao tác
              </ActionMenu.Button>
              <ActionMenu.Overlay width="auto">
                <ActionList>
                  <ActionList.Item
                    onSelect={handleExportBaoCao}
                    disabled={selectedHocSinhIds.length === 0 || isExporting}
                  >
                    <ActionList.LeadingVisual>
                      <DownloadIcon />
                    </ActionList.LeadingVisual>
                  Xuất biên nhận ({selectedHocSinhIds.length})
                  </ActionList.Item>
                  <ActionList.Item
                    onSelect={handleExportBanGiao}
                    disabled={selectedHocSinhIds.length === 0 || isExporting}
                  >
                    <ActionList.LeadingVisual>
                      <DownloadIcon />
                    </ActionList.LeadingVisual>
                  Xuất biên bản bàn giao ({selectedHocSinhIds.length})
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          <SelectBoxTrangThaiHocSinhMultiple
            selectedValue={dm_trangthaihocsinh_ids}
            onSelectionChanged={(ids) => {
              setDmTrangThaiIds(ids);
              onValueChanged &&
                onValueChanged({
                  dm_trangthaihocsinh_ids: ids.join(","),
                });
            }}
            maxWidth={200}
            isShowClearBtn={true}
            hocSinhs={allHocSinhs}
          />

          <Box sx={{ minWidth: "150px" }}>
            <ComboboxTruong
              value={dm_truong_selected_id}
              onValueChanged={(value) => {
                dispatch(
                  actions.common.changedValueHeaderComboboxTruong(value)
                );
              }}
              isShowClearButton={true}
            />
          </Box>

          <Button
            text="Base.Label.Refresh"
            leadingVisual={SyncIcon}
            size="medium"
            onClick={() => {
              handleReload();
            }}
          />

          <TextInput
            leadingVisual={SearchIcon}
            placeholder="Tìm kiếm học sinh, giấy tờ..."
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width: "300px" }}
          />
        </Box>
      </Box>

      <DataTable
        height={`${window.innerHeight - 210}px`}
        data={filteredHocSinhs.map((hs) => ({
          ...hs,
          id: hs.hocsinh_id,
        }))}
        isLoading={status === ePageBaseStatus.is_loading}
        paging={{
          enable: true,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        expandDetailRowTemplate={(data: any) =>
          renderDocumentList(data as HocSinh)
        }
        onExpandedRowClick={(id: any) => {
          if (expandedStudentId === id) {
            setExpandedStudentId(null);
          } else {
            setExpandedStudentId(id);
          }
        }}
        expandedRowKeys={expandedStudentId ? [expandedStudentId] : []}
        selection={{
          mode: "multiple",
          keyExpr: "id",
          selectedRowKeys: selectedHocSinhIds,
          onSelectionChanged: handleSelectionChanged,
        }}
        columns={[
          {
            id: "giayto_count",
            width: "80px",
            caption: translate("Giấy tờ"),
            align: "center",
            fixed: true,
            cellRender: (data: HocSinh) => {
              const key = `${data.nam_hoc || ""}_${data.dm_truong_id || 0}_${
                data.dm_khoi_id || 0
              }_${data.dm_he_id || 0}`;

              const totalRequiredTypes =
                loaiGiayToTheoTruongMap[key]?.length || 0;

              const submittedTypes = data.danhSachGiayTo?.filter(
                (gt) => gt.is_danop === true
              ).length || 0;

              // Chỉ đếm file của những giấy tờ đã nộp (is_danop === true)
              const submittedGiayTo = data.danhSachGiayTo?.filter(
                (gt) => gt.is_danop === true
              ) || [];
              const fileCount = countTotalFiles(submittedGiayTo);

              let variant: "success" | "danger" | "attention" = "danger";
              if (
                submittedTypes === totalRequiredTypes &&
                totalRequiredTypes > 0
              ) {
                variant = "success";
              } else if (submittedTypes > 0) {
                variant = "attention";
              }

              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRowClick(data)}
                >
                  <Label variant={variant} sx={{ cursor: "pointer" }}>
                    {submittedTypes}/{totalRequiredTypes}
                  </Label>
                  <Box
                    sx={{ fontSize: "12px", mt: 1, color: "text.secondary" }}
                  >
                    {fileCount} file
                  </Box>
                </Box>
              );
            },
          },
          {
            dataField: "ho_ten",
            caption: translate("Học sinh"),
            width: "200px",
            cellRender: (data: HocSinh) => {
              return (
                <Box sx={{ display: "flex" }}>
                  <Box sx={{ mr: 2, mt: 1 }}>
                    <PreviewableAvatar
                      avatarUrl={data.avatar}
                      fullName={data.ho_ten}
                      size="large"
                    />
                  </Box>
                  <Box sx={{ display: "grid", flex: 1 }}>
                    <Box
                      sx={{
                        fontWeight: 600,
                        color: "inherit",
                      }}
                    >
                      {data.ho_ten}
                    </Box>
                    <Box sx={{ color: "fg.muted" }}>{data.ma_hs}</Box>
                  </Box>
                </Box>
              );
            },
          },
          {
            dataField: "thong_lop",
            caption: translate("Trường lớp"),
            width: "200px",
            cellRender: (data: HocSinh) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "baseline",
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <Box sx={{ fontWeight: 600 }}>{data.ten_lop}</Box>
                  </Box>
                  <Box sx={{ color: "fg.muted" }}>
                    {data.ten_khoi}, Hệ {data.ten_he}
                  </Box>
                </Box>
              );
            },
          },

          {
            id: "danh_sach_giay_to",
            caption: translate("Danh sách giấy tờ"),
            cellRender: (data: HocSinh) => {
              const key = `${data.nam_hoc || ""}_${data.dm_truong_id || 0}_${
                data.dm_khoi_id || 0
              }_${data.dm_he_id || 0}`;
              const loaiGiayToTheoTruong = loaiGiayToTheoTruongMap[key] || [];

              if (!loaiGiayToTheoTruong || loaiGiayToTheoTruong.length === 0) {
                return (
                  <Box
                    sx={{
                      color: "text.secondary",
                      fontStyle: "italic",
                      cursor: "pointer",
                      flexGrow: 1,
                    }}
                    onClick={() => handleRowClick(data)}
                  >
                    Chưa có loại giấy tờ nào được định nghĩa cho trường này
                  </Box>
                );
              }

              const isExpanded = expandedLoaiGiayToStudentIds.has(
                data.hocsinh_id
              );
              const loaiGiayToToShow = isExpanded
                ? loaiGiayToTheoTruong
                : loaiGiayToTheoTruong.slice(0, 3);
              const hasMore = loaiGiayToTheoTruong.length > 3;

              const handleToggleExpand = (e: React.MouseEvent) => {
                e.stopPropagation();
                setExpandedLoaiGiayToStudentIds((prev) => {
                  const newSet = new Set(prev);
                  if (newSet.has(data.hocsinh_id)) {
                    newSet.delete(data.hocsinh_id);
                  } else {
                    newSet.add(data.hocsinh_id);
                  }
                  return newSet;
                });
              };

              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    cursor: "pointer",
                    flexGrow: 1,
                  }}
                  onClick={() => handleRowClick(data)}
                >
                  {loaiGiayToToShow.map((loaiGiayTo: any, index: number) => {
                    const giayToNop = data.danhSachGiayTo?.find(
                      (gt) => gt.loai_giay_to === loaiGiayTo.loai_giay_to
                    );
                    const isSubmitted = giayToNop?.is_danop === true;

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderBottom:
                            index < loaiGiayToToShow.length - 1
                              ? "1px solid #eaecef"
                              : "none",
                          pb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <FileIcon size={14} />
                          <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
                            <TextWithEllipse
                              text={loaiGiayTo.loai_giay_to}
                              lineNumber={1}
                              allowToggleText={true}
                            />
                          </Box>
                        </Box>
                        {isSubmitted ? (
                          <Label
                            sx={{ fontSize: "11px", ml: 1 }}
                            variant="success"
                          >
                            Đã nộp
                          </Label>
                        ) : (
                          <Label
                            sx={{ fontSize: "11px", ml: 1 }}
                            variant="danger"
                          >
                            Chưa nộp
                          </Label>
                        )}
                      </Box>
                    );
                  })}
                  {hasMore && (
                    <Box
                      sx={{
                        color: "text.secondary",
                        fontSize: "12px",
                        fontStyle: "italic",
                        cursor: "pointer",
                        "&:hover": {
                          color: "accent.fg",
                          textDecoration: "underline",
                        },
                      }}
                      onClick={handleToggleExpand}
                    >
                      {isExpanded
                        ? "Thu gọn"
                        : `...và ${loaiGiayToTheoTruong.length - 3} loại khác`}
                    </Box>
                  )}
                </Box>
              );
            },
          },
          {
            id: "chi_tiet_file",
            caption: translate("Chi tiết file đính kèm"),
            cellRender: (data: HocSinh) => {
              const hasAttachments = data.danhSachGiayTo?.some(
                (gt) => gt.danhSachChiTiet && gt.danhSachChiTiet.length > 0
              );

              if (!hasAttachments) {
                return (
                  <Box
                    sx={{
                      color: "text.secondary",
                      fontStyle: "italic",
                      cursor: "pointer",
                      flexGrow: 1,
                    }}
                    onClick={() => handleRowClick(data)}
                  >
                    Chưa có file đính kèm
                  </Box>
                );
              }

              const allFiles: Array<GiayToChiTiet & { loai_giay_to: string }> =
                [];

              data.danhSachGiayTo?.forEach((giayTo) => {
                if (
                  giayTo.danhSachChiTiet &&
                  giayTo.danhSachChiTiet.length > 0
                ) {
                  giayTo.danhSachChiTiet.forEach((chiTiet) => {
                    allFiles.push({
                      ...chiTiet,
                      loai_giay_to: giayTo.loai_giay_to,
                    });
                  });
                }
              });

              const filesToShow = allFiles.slice(0, 2);
              const totalFiles = allFiles.length;

              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flexGrow: 1,
                  }}
                >
                  {filesToShow.map((file, index) => {
                    const isImage = isImageFile(file.ten_file);
                    const fileUrl = getCorrectFileUrl(file.duong_dan);

                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          pb: 1,
                          borderBottom:
                            index < filesToShow.length - 1
                              ? "1px solid #eaecef"
                              : "none",
                        }}
                      >
                        {isImage ? (
                          <img
                            src={fileUrl}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                            alt={file.ten_file}
                          />
                        ) : (
                          <FileIcon size={16} />
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                          }}
                        >
                          <Text
                            as="a"
                            href={fileUrl}
                            target="_blank"
                            sx={{
                              color: "#0366d6",
                              textDecoration: "none",
                              fontWeight: "500",
                              "&:hover": { textDecoration: "underline" },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {truncateText(file.ten_file, 50)}
                          </Text>
                          <Box
                            sx={{ fontSize: "12px", color: "text.secondary" }}
                          >
                            {truncateText(file.loai_giay_to, 50)}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}

                  {totalFiles > 5 && (
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "text.secondary",
                        fontStyle: "italic",
                        cursor: "pointer",
                      }}
                      onClick={() => handleRowClick(data)}
                    >
                      ...và {totalFiles - 5} file khác
                    </Box>
                  )}
                </Box>
              );
            },
          },
        ]}
      />
      {isShowModal && (
        <HocSinhProfile
          id={hocSinhEditing.id}
          onClose={() => {
            setIsShowModal(false);
          }}
          onSuccess={() => {
            handleReload();
            setIsShowModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default HocSinhGiayTo;
